import { SingleAndMultiSelectFieldOption } from 'module/api/field/types';
import { useTranslation } from 'module/hooks/use-translation';
import { AddOptionsInput } from 'module/ui/components/atoms/choices/add-option';
import { SortableOptions } from 'module/ui/components/atoms/choices/list';
import { Form } from 'module/ui/components/atoms/form';
import { OutsideClickHandler } from 'module/ui/components/atoms/outside-click-handler';
import { TableCellType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { Select } from 'module/ui/components/atoms/select/select';
import { faCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { InputField, Textarea } from '@rocketlane/ui';
import React, {
  useEffect,
  useRef,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { DoneButton } from '../custom-section/ui/shared/section-styles';

interface TableHeaderPopoverProps {
  node: any;
  headerType: TableCellType.ROW | TableCellType.COLUMN;
  headerText: string;
  onSave: (
    columnName: string,
    columnType: {
      value: TableDataType;
      label: string;
    },
    aiContext: string,
    headerType: TableCellType.ROW | TableCellType.COLUMN,
    choices: SingleAndMultiSelectFieldOption[],
  ) => void;
  onCancel: () => void;
  handleClickOutside: (event: MouseEvent) => void;
  /** The header column's own header cell (row 0, col 0 of a TABLE section)
   * labels the row-header column below it, so it only ever holds plain
   * identifiers — restrict it to Text/Number instead of the full type list. */
  restrictToTextAndNumber?: boolean;
}

const StyledFormItem = styled(Form.Item)`
  padding-bottom: 0 !important;

  /* Ensure select components can overflow properly */
  .ant-select {
    z-index: 1;
  }
`;

const StyledChoicesFormItem = styled(Form.Item)`
  padding-bottom: 0 !important;

  .ant-form-item {
    margin-bottom: 0 !important;
    padding-bottom: var(--spacing-02) !important;
  }
`;

const PopoverContainer = styled.div`
  background: var(--scarlet-gray-20);
  border: 1px solid #ccc;
  border-radius: 4px;
  padding: 16px;
  font-size: 14px;
  color: #333;
  min-width: 382px;
  word-wrap: break-word;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  justify-content: flex-end;
`;

const ChoicesContainer = styled.div`
  gap: var(--spacing-02);
  display: flex;
  flex-direction: column;
  margin-top: var(--spacing-03);
`;

export const TableHeaderPopover: React.FC<TableHeaderPopoverProps> = ({
  headerType,
  node,
  handleClickOutside,
  onSave,
  onCancel,
  restrictToTextAndNumber = false,
}) => {
  const [form] = Form.useForm();
  const inputRef = useRef<HTMLInputElement>(null);
  const [columnType, setColumnType] = useState<TableDataType>(
    node.attrs?.config?.dataType,
  );
  const [choices, setChoices] = useState<SingleAndMultiSelectFieldOption[]>([]);
  const selectRef = useRef<any>(null);

  const handleUpdateOptions = (options: any[]) => {
    setChoices(options);
  };

  const handleEditInputOption = (updatedOption: any) => {
    setChoices((prev) =>
      prev.map((option) =>
        option.value === updatedOption.value ? updatedOption : option,
      ),
    );
  };

  // Move translations to component level
  const textLabel = useTranslation('fields.label.text');
  const numberLabel = useTranslation('fields.label.number');
  const booleanLabel = useTranslation('fields.label.boolean');
  const dateLabel = useTranslation('fields.label.date');
  const columnNameLabel = useTranslation('table_header_popover.column_name');
  const rowNameLabel = useTranslation('table_header_popover.row_name');
  const columnTypeLabel = useTranslation('table_header_popover.column_type');
  const aiContextLabel = useTranslation('table_header_popover.ai_context');
  const columnNamePlaceholder = useTranslation(
    'table_toolbar.column_name_placeholder',
  );
  const rowNamePlaceholder = useTranslation(
    'table_toolbar.row_name_placeholder',
  );
  const aiContextColumnPlaceholder = useTranslation(
    'table_toolbar.ai_context_column_placeholder',
  );
  const aiContextRowPlaceholder = useTranslation(
    'table_toolbar.ai_context_row_placeholder',
  );
  const singleSelectLabel = useTranslation('fields.label.single_select');
  const multiSelectLabel = useTranslation('fields.label.multi_select');
  const columnOptions = useMemo(() => {
    const textAndNumber = [
      { value: TableDataType.TEXT, label: textLabel },
      { value: TableDataType.NUMBER, label: numberLabel },
    ];

    if (restrictToTextAndNumber) {
      return textAndNumber;
    }

    return [
      ...textAndNumber,
      { value: TableDataType.BOOLEAN, label: booleanLabel },
      { value: TableDataType.DATE, label: dateLabel },
      { value: TableDataType.SingleSelect, label: singleSelectLabel },
      { value: TableDataType.MultiSelect, label: multiSelectLabel },
    ];
  }, [
    textLabel,
    numberLabel,
    booleanLabel,
    dateLabel,
    singleSelectLabel,
    multiSelectLabel,
    restrictToTextAndNumber,
  ]);

  const columnName = headerType === TableCellType.COLUMN ? 'Column' : 'Row';

  useEffect(() => {
    // Set initial form values
    form.setFieldsValue({
      columnName: node.attrs?.config?.name ?? columnName,
      columnType: columnOptions.find(
        (type) => type.value === node.attrs?.config?.dataType,
      ),
      aiContext: node.attrs?.config?.aiContext,
    });

    if (node.attrs?.config?.choices) {
      setChoices(
        node.attrs?.config?.choices?.map((choice) => ({
          value: choice,
          label: choice,
        })),
      );
    }
    // Focus the input when the popover opens
    if (inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [form, node, columnOptions, columnName]);

  // Force menu to open after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      // Try to programmatically open the menu
      if (selectRef.current && !columnType) {
        selectRef.current.focus();
        selectRef.current.onMenuOpen();
      }
    }, 100);

    return () => clearTimeout(timer);
  }, [columnType]);

  const validateForm = async (): Promise<boolean> => {
    try {
      await form.validateFields();

      return true;
    } catch {
      return false;
    }
  };

  const handleSave = async () => {
    if (await validateForm()) {
      const values = form.getFieldsValue();

      // Include boolean choices if column type is boolean
      const saveData = {
        columnName: values.columnName,
        columnType: values.columnType,
        aiContext: values.aiContext,
        headerType,
        ...((values.columnType?.value === TableDataType.BOOLEAN ||
          values.columnType?.value === TableDataType.SingleSelect ||
          values.columnType?.value === TableDataType.MultiSelect) && {
          choices: choices ?? [],
        }),
      };

      onSave(
        saveData.columnName,
        saveData.columnType,
        saveData.aiContext,
        saveData.headerType,
        saveData.choices ?? [],
      );
    }
  };
  const addOption = useCallback(
    (option) => {
      const newOptions = choices.concat({
        ...option,
        value:
          choices.reduce((max, option) => Math.max(max, option.value), 0) + 1,
        position: choices.length + 1,
      });

      setChoices(newOptions);
    },
    [choices, setChoices],
  );

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      onCancel();
    }
  };

  return (
    <OutsideClickHandler
      onOutsideClick={handleClickOutside}
      className="enhanced-table-header-popover"
    >
      <PopoverContainer>
        {/* Form */}
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSave}
          style={{ marginBottom: '0' }}
        >
          {/* Column Type Field */}
          {headerType === TableCellType.COLUMN && (
            <StyledFormItem
              name="columnType"
              label={columnTypeLabel}
              rules={[{ required: true, message: 'Column type is required' }]}
            >
              <Select
                ref={selectRef}
                menuPortalTarget={document.body}
                styles={{
                  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                }}
                options={columnOptions}
                value={form.getFieldValue('columnType')}
                isSearchable={false}
                isClearable={false}
                placeholder="Select column type..."
                onChange={(value) => {
                  setColumnType(value.value);
                  if (value.value === TableDataType.BOOLEAN) {
                    setChoices([
                      { value: 1, label: 'Yes' },
                      { value: 2, label: 'No' },
                    ]);
                  } else if (
                    [
                      TableDataType.SingleSelect,
                      TableDataType.MultiSelect,
                    ].includes(value.value)
                  ) {
                    setChoices([
                      { value: 1, label: 'HubSpot' },
                      { value: 2, label: 'Salesforce' },
                    ]);
                  }
                }}
              />
            </StyledFormItem>
          )}
          {(headerType === TableCellType.COLUMN &&
            (node.attrs?.config?.dataType ||
              form.getFieldValue('columnType'))) ||
          headerType === TableCellType.ROW ? (
            <>
              <StyledFormItem
                name="columnName"
                label={
                  headerType === TableCellType.COLUMN
                    ? columnNameLabel
                    : rowNameLabel
                }
                rules={[{ required: true, message: 'Column name is required' }]}
              >
                <InputField
                  ref={inputRef}
                  placeholder={
                    headerType === TableCellType.COLUMN
                      ? columnNamePlaceholder
                      : rowNamePlaceholder
                  }
                  onKeyDown={handleKeyDown}
                />
              </StyledFormItem>
              {headerType === TableCellType.COLUMN &&
                [
                  TableDataType.BOOLEAN,
                  TableDataType.SingleSelect,
                  TableDataType.MultiSelect,
                ].includes(columnType) && (
                  <StyledChoicesFormItem
                    label={
                      <FormattedMessage id="rich_text_editor_sections_extract_info_section.choices" />
                    }
                  >
                    <ChoicesContainer>
                      <SortableOptions
                        shouldDrag={false}
                        validateForm={validateForm}
                        options={choices}
                        canDeleteChoices={TableDataType.BOOLEAN !== columnType}
                        removeOption={() => {}} // No remove option for boolean
                        updateOptions={handleUpdateOptions}
                        editInputOption={handleEditInputOption}
                      />
                      {[
                        TableDataType.SingleSelect,
                        TableDataType.MultiSelect,
                      ].includes(columnType) ? (
                        <AddOptionsInput
                          shouldValidate={false}
                          existingOptions={choices}
                          addOption={addOption}
                          addedAsFormItem={false}
                        />
                      ) : null}
                      {/* No AddOptionsInput for boolean - fixed Yes/No options */}
                    </ChoicesContainer>
                  </StyledChoicesFormItem>
                )}
              {/* AI Context Field */}
              <StyledFormItem
                name="aiContext"
                label={aiContextLabel}
                rules={[{ required: false, message: 'AI context is required' }]}
              >
                <Textarea
                  placeholder={
                    headerType === TableCellType.COLUMN
                      ? aiContextColumnPlaceholder
                      : aiContextRowPlaceholder
                  }
                  rows={3}
                  style={{ resize: 'vertical' }}
                />
              </StyledFormItem>

              {/* Button container */}
              <ButtonContainer>
                {/* Cancel button */}
                <DoneButton variant="secondary" onClick={onCancel}>
                  <FormattedMessage id="cancel" />
                </DoneButton>
                {/* Save button */}
                <DoneButton
                  variant="primary"
                  onClick={() => {
                    handleSave();
                  }}
                >
                  <FontAwesomeIcon icon={faCheck} />
                  <FormattedMessage id="rich_text_editor.sections.done" />
                </DoneButton>
              </ButtonContainer>
            </>
          ) : null}
        </Form>
      </PopoverContainer>
    </OutsideClickHandler>
  );
};
